/**
 * GPS-/navigasjonshjelpere for fart (SOG) og kurs (COG).
 *
 * Rene funksjoner — enkle å teste og uavhengige av UI.
 */

const R_EARTH = 6_371_000; // meter
const toRad = (d: number) => (d * Math.PI) / 180;
const toDeg = (r: number) => (r * 180) / Math.PI;

export interface LatLon {
  lat: number;
  lon: number;
}

/** Storsirkel-avstand mellom to punkt i meter (haversine). */
export function metersBetween(a: LatLon, b: LatLon): number {
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const la1 = toRad(a.lat);
  const la2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLon / 2) ** 2;
  return 2 * R_EARTH * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Initial peiling fra a til b, i grader 0–360 (0 = nord, med klokka). */
export function bearingDeg(a: LatLon, b: LatLon): number {
  const la1 = toRad(a.lat);
  const la2 = toRad(b.lat);
  const dLon = toRad(b.lon - a.lon);
  const y = Math.sin(dLon) * Math.cos(la2);
  const x = Math.cos(la1) * Math.sin(la2) - Math.sin(la1) * Math.cos(la2) * Math.cos(dLon);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

export const MS_TO_KNOTS = 1.943_844;
export const MS_TO_KMH = 3.6;

export function msToKnots(ms: number): number {
  return ms * MS_TO_KNOTS;
}
export function msToKmh(ms: number): number {
  return ms * MS_TO_KMH;
}

/** Norsk 8-streks kompassretning for en gradverdi. */
export function compassPointNo(deg: number): string {
  const points = ['N', 'NØ', 'Ø', 'SØ', 'S', 'SV', 'V', 'NV'];
  const idx = Math.round((((deg % 360) + 360) % 360) / 45) % 8;
  return points[idx]!;
}

/**
 * Sirkulær glatting av en vinkel (grader) for å unngå nervøs nål og
 * 359°→1°-hopp. Returnerer ny glattet vinkel gitt forrige vektor.
 */
export interface AngleVec {
  x: number;
  y: number;
}
export function smoothAngle(
  prev: AngleVec | null,
  deg: number,
  alpha: number,
): { angle: number; vec: AngleVec } {
  const r = toRad(deg);
  const cur = { x: Math.cos(r), y: Math.sin(r) };
  const vec = prev
    ? { x: prev.x * (1 - alpha) + cur.x * alpha, y: prev.y * (1 - alpha) + cur.y * alpha }
    : cur;
  const angle = (toDeg(Math.atan2(vec.y, vec.x)) + 360) % 360;
  return { angle, vec };
}

/** Eksponentielt glidende snitt for en skalar (f.eks. fart). */
export function ema(prev: number | null, value: number, alpha: number): number {
  return prev === null ? value : prev * (1 - alpha) + value * alpha;
}
